import { useState } from 'react';
import { Box, Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FormHeader } from '../components/FormHeader';
import { CommunicationsList } from '../features/communications/CommunicationsList';
import { useCommunications } from '../features/communications/useCommunications';
import type { Message, PartyKey } from '../features/communications/sample';
import {
  NextActionsPanel,
  RelatedItemsPanel,
  ShipmentInfoPanel,
  StatusPanel,
} from '../features/communications/SidePanels';
import { ThreadView } from '../features/communications/ThreadView';

const GAP = { xs: 1.5, md: 2 };

/** One shipment's conversations. Wide: shipments on the left, the conversation in the middle, facts on the right. Phone: just the conversation. */
export function ConversationPage() {
  const { srb } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('lg'));
  const xwide = useMediaQuery(theme.breakpoints.up('xl'));
  const [query] = useSearchParams();
  const [party, setParty] = useState<PartyKey>((query.get('party') as PartyKey | null) ?? 'Overseas Agent');
  const [notes, setNotes] = useState<Record<string, Message[]>>({});

  const { rows } = useCommunications();
  const open = rows.find((r) => r.conv.srb === srb);
  if (!open) return <Navigate to="/communications" replace />;

  const key = `${open.conv.srb}:${party}`;
  const addNote = (text: string) =>
    setNotes((all) => ({
      ...all,
      [key]: [
        ...(all[key] ?? []),
        {
          id: `n${Date.now()}`,
          date: 'Today',
          time: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Amman' }),
          from: 'You',
          text,
          internal: true,
        },
      ],
    }));
  const thread = (
    <ThreadView
      conv={open.conv}
      ship={open.ship}
      party={party}
      onParty={setParty}
      notes={notes[key] ?? []}
      onAddNote={addNote}
    />
  );
  const side = (
    <Stack spacing={GAP}>
      <ShipmentInfoPanel ship={open.ship} />
      <StatusPanel conv={open.conv} party={party} onParty={setParty} />
      <NextActionsPanel conv={open.conv} />
      <RelatedItemsPanel conv={open.conv} />
    </Stack>
  );

  return (
    <Stack spacing={GAP}>
      <FormHeader
        back={{ to: '/communications', label: 'Back to Communications' }}
        title="Communications"
        subtitle="View and manage communication summaries for each shipment."
      />
      {wide ? (
        <Box
          sx={{
            display: 'grid',
            gap: GAP,
            alignItems: 'start',
            gridTemplateColumns: xwide
              ? 'minmax(280px, 340px) minmax(0, 1fr) 340px'
              : 'minmax(280px, 340px) minmax(0, 1fr)',
          }}
        >
          <Box sx={{ gridRow: xwide ? 'auto' : '1 / span 2' }}>
            <CommunicationsList
              rows={rows}
              selected={open.conv.srb}
              onSelect={(next) => void navigate(`/communications/${next}`)}
            />
          </Box>
          {thread}
          {side}
        </Box>
      ) : (
        <>
          {thread}
          {side}
        </>
      )}
    </Stack>
  );
}
